import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { AppShell } from "../components/AppShell";
import { LineField } from "../components/LineField";
import { EmptyState } from "../components/EmptyState";
import { PaginationBar, usePagination } from "../components/Pagination";
import { useAuth } from "../hooks/useAuth";
import { useT } from "../i18n/LanguageProvider";
import {
  cancelCandidateRequest,
  listCandidateRequests,
  requestCandidateProfile,
  listProfileRequests,
  acceptProfileRequest,
  declineProfileRequest,
} from "../api/candidates";
import { C, font } from "../styles/tokens";
import { SafeLogo } from "../components/SafeLogo";

/**
 * CandidateRequestsPage - shared between two roles with different views.
 *
 * Organisation officers:
 *   - See a list of all profile requests they've sent
 *   - Can send a new request by entering a candidate's email
 *
 * Professionals:
 *   - See incoming profile requests from organisations
 *   - Can accept (share access) or decline each request
 */
export function CandidateRequestsPage() {
  const { localUser } = useAuth();
  const isOrganisation = localUser?.type === "ORGANISATION_OFFICER";
  const isProfessional = localUser?.type === "PROFESSIONAL";

  if (isOrganisation) return <OrganisationView />;
  if (isProfessional) return <ProfessionalView />;
  return (
    <AppShell>
      <div style={cardStyle}>
        This page is for organisation officers and professionals.
      </div>
    </AppShell>
  );
}

// =============================================================================
// ORGANISATION VIEW -- send and track requests
// =============================================================================

function OrganisationView() {
  const t = useT();
  const [requests, setRequests] = useState([]);
  const pager = usePagination(requests, 10);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    candidateEmail: "",
    appliedForRole: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [formError, setFormError] = useState(null);

  async function reload() {
    setLoading(true);
    try {
      const data = await listCandidateRequests();
      setRequests(data);
      setError(null);
    } catch (err) {
      setError(err.message ?? t("common.somethingWrong"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
  }, []);

  const [cancellingId, setCancellingId] = useState(null);

  async function handleCancel(req) {
    if (
      !window.confirm(
        `Cancel the request to ${req.candidateEmail}? They can no longer answer it.`,
      )
    ) {
      return;
    }
    setCancellingId(req.id);
    try {
      await cancelCandidateRequest(req.id);
      await reload();
    } catch (err) {
      setError(err.message ?? t("common.somethingWrong"));
    } finally {
      setCancellingId(null);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setFormError(null);
    setSubmitting(true);
    try {
      await requestCandidateProfile({
        candidateEmail: form.candidateEmail.trim().toLowerCase(),
        appliedForRole: form.appliedForRole.trim() || undefined,
        message: form.message.trim() || undefined,
      });
      setForm({ candidateEmail: "", appliedForRole: "", message: "" });
      setShowForm(false);
      await reload();
    } catch (err) {
      setFormError(err.message ?? t("common.somethingWrong"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AppShell>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
          gap: 12,
        }}
      >
        <div>
          <h1
            style={{
              fontSize: 24,
              fontWeight: 700,
              margin: "0 0 4px",
              letterSpacing: -0.4,
            }}
          >
            Candidate profiles
          </h1>
          <p style={{ fontSize: 13, color: C.textMed, margin: 0 }}>
            Ask a candidate for their verified profile by email. Once they
            accept, open it here. Every view is logged.
          </p>
        </div>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            style={{
              padding: "10px 22px",
              fontSize: 13,
              fontWeight: 600,
              color: "white",
              background: C.blue,
              border: 0,
              borderRadius: 4,
              cursor: "pointer",
              fontFamily: font,
              whiteSpace: "nowrap",
            }}
          >
            Request a profile
          </button>
        )}
      </div>

      {/* Request form: an organisation asks a candidate for their profile. */}
      {showForm && (
        <section
          style={{
            ...cardStyle,
            marginBottom: 16,
            background: C.blueLight + "33",
            borderColor: C.blue + "33",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              marginBottom: 12,
            }}
          >
            <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>
              Request a candidate profile
            </h2>
            <button
              onClick={() => {
                setShowForm(false);
                setFormError(null);
              }}
              style={{
                background: "transparent",
                border: 0,
                color: C.textLight,
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: font,
              }}
            >
              {t("common.cancel")}
            </button>
          </div>
          <p
            style={{
              fontSize: 12,
              color: C.textMed,
              lineHeight: 1.5,
              margin: "0 0 16px",
            }}
          >
            Enter the email address the candidate used when they applied. If
            they have a TickAndPass profile and accept your request, you'll be
            able to view their verified credentials.
          </p>
          <form onSubmit={handleSubmit}>
            <LineField
              label="Candidate's email"
              type="email"
              placeholder="candidate@example.com"
              value={form.candidateEmail}
              onChange={(e) =>
                setForm((f) => ({ ...f, candidateEmail: e.target.value }))
              }
              required
            />
            <LineField
              label="Role they applied for (optional)"
              placeholder="Software Engineer, Data Analyst, etc."
              value={form.appliedForRole}
              onChange={(e) =>
                setForm((f) => ({ ...f, appliedForRole: e.target.value }))
              }
            />
            <LineField
              label="Message to the candidate (optional)"
              as="textarea"
              placeholder="We're reviewing your application for the Senior Engineer role and would like to verify your credentials via TickAndPass."
              value={form.message}
              onChange={(e) =>
                setForm((f) => ({ ...f, message: e.target.value }))
              }
            />
            {formError && (
              <div
                style={{
                  fontSize: 13,
                  color: C.red,
                  background: C.redLight,
                  padding: "10px 12px",
                  borderRadius: 4,
                  marginTop: 8,
                }}
              >
                {formError}
              </div>
            )}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: 12,
              }}
            >
              <button
                type="submit"
                disabled={submitting}
                style={{
                  padding: "10px 22px",
                  fontSize: 13,
                  fontWeight: 600,
                  color: "white",
                  background: submitting ? C.textLight : C.blue,
                  border: 0,
                  borderRadius: 4,
                  cursor: submitting ? "default" : "pointer",
                  fontFamily: font,
                }}
              >
                {submitting ? "Sending..." : "Send request"}
              </button>
            </div>
          </form>
        </section>
      )}

      {error && (
        <div style={{ ...cardStyle, color: C.red, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {/* Request list */}
      <section style={{ ...cardStyle }}>
        {loading ? (
          <div style={{ fontSize: 13, color: C.textMed }}>
            {t("common.loading")}
          </div>
        ) : requests.length === 0 ? (
          <EmptyState
            kind="search"
            title="No profile requests yet"
            body="Ask a candidate for their verified profile with Request a profile. Their answer shows up here."
            size="large"
          />
        ) : (
          <>
            {pager.pageItems.map((req, i) => (
              <OrgRequestRow
                key={req.id}
                req={req}
                showDivider={i > 0}
                onCancel={() => handleCancel(req)}
                cancelling={cancellingId === req.id}
              />
            ))}
            <PaginationBar
              pager={pager}
              itemLabel="requests"
              style={{
                margin: "12px -24px -24px",
                borderRadius: "0 0 8px 8px",
              }}
            />
          </>
        )}
      </section>
    </AppShell>
  );
}

// A request as the organisation sees it: the candidate is named only while
// they share their profile, and TickAndPass never says whether the email
// has an account. A request still waiting can be cancelled.
function OrgRequestRow({ req, showDivider, onCancel, cancelling }) {
  const resolved = !!req.candidateProfessional;
  const statusConfig = {
    SENT: { bg: C.amberLight, color: C.amber, label: "Pending" },
    ACCEPTED: { bg: C.greenLight, color: C.green, label: "Accepted" },
    DECLINED: { bg: C.redLight, color: C.red, label: "Declined" },
    EXPIRED: { bg: C.divider, color: C.textMed, label: "Expired" },
    CANCELLED: { bg: C.divider, color: C.textMed, label: "Cancelled" },
  }[req.status] ?? { bg: C.divider, color: C.textMed, label: req.status };

  return (
    <div
      style={{
        padding: "14px 0",
        borderTop: showDivider ? `1px solid ${C.divider}` : "none",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          alignItems: "flex-start",
        }}
      >
        <div style={{ flex: 1 }}>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 8,
              marginBottom: 4,
            }}
          >
            <span style={{ fontSize: 14, fontWeight: 600, color: C.textDark }}>
              {resolved
                ? `${req.candidateProfessional.firstName} ${req.candidateProfessional.lastName}`
                : req.candidateEmail}
            </span>
            <StatusPill
              color={statusConfig.color}
              bg={statusConfig.bg}
              label={statusConfig.label}
            />
          </div>
          {resolved && (
            <div style={{ fontSize: 12, color: C.textMed }}>
              {req.candidateEmail}
              {req.candidateProfessional.city &&
                ` - ${req.candidateProfessional.city}`}
            </div>
          )}
          {req.appliedForRole && (
            <div style={{ fontSize: 12, color: C.textLight }}>
              For: {req.appliedForRole}
            </div>
          )}
          <div style={{ fontSize: 11, color: C.textLight, marginTop: 2 }}>
            Sent {formatDate(req.createdAt)}
            {req.respondedAt && <> - responded {formatDate(req.respondedAt)}</>}
            {req.status === "SENT" && <> - they have 30 days to answer</>}
          </div>
        </div>
        {req.status === "SENT" && (
          <button
            type="button"
            onClick={onCancel}
            disabled={cancelling}
            className="px-3.5 py-1.5 text-xs font-semibold rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50 whitespace-nowrap"
          >
            {cancelling ? "Cancelling..." : "Cancel request"}
          </button>
        )}
        {req.status === "ACCEPTED" && req.candidateProfessional && (
          <a
            href={`/candidates/requests/${req.id}/profile`}
            style={{
              padding: "6px 14px",
              fontSize: 12,
              fontWeight: 600,
              color: "white",
              background: C.green,
              border: 0,
              borderRadius: 4,
              textDecoration: "none",
              fontFamily: font,
              whiteSpace: "nowrap",
            }}
          >
            View profile
          </a>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// PROFESSIONAL VIEW -- see and respond to incoming requests
// =============================================================================

function ProfessionalView() {
  const t = useT();
  const [requests, setRequests] = useState([]);
  const pager = usePagination(requests, 10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actingId, setActingId] = useState(null);

  async function reload() {
    setLoading(true);
    try {
      const data = await listProfileRequests();
      setRequests(data);
      setError(null);
    } catch (err) {
      setError(err.message ?? t("common.somethingWrong"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
  }, []);

  async function handleAccept(id) {
    setActingId(id);
    try {
      await acceptProfileRequest(id);
      await reload();
    } catch (err) {
      alert(err.message ?? t("common.somethingWrong"));
    } finally {
      setActingId(null);
    }
  }

  async function handleDecline(id) {
    setActingId(id);
    try {
      await declineProfileRequest(id);
      await reload();
    } catch (err) {
      alert(err.message ?? t("common.somethingWrong"));
    } finally {
      setActingId(null);
    }
  }

  return (
    <AppShell>
      <div style={{ marginBottom: 16 }}>
        <h1
          style={{
            fontSize: 24,
            fontWeight: 700,
            margin: "0 0 4px",
            letterSpacing: -0.4,
          }}
        >
          Profile requests
        </h1>
        <p style={{ fontSize: 13, color: C.textMed, margin: 0 }}>
          Organisations that want to view your verified credentials.
        </p>
      </div>

      {error && (
        <div style={{ ...cardStyle, color: C.red, marginBottom: 16 }}>
          {error}
        </div>
      )}

      <section style={cardStyle}>
        {loading ? (
          <div style={{ fontSize: 13, color: C.textMed }}>
            {t("common.loading")}
          </div>
        ) : requests.length === 0 ? (
          <EmptyState
            kind="inbox"
            title="No incoming requests"
            body="When an organisation wants to view your verified profile, the request shows up here. You choose who to share with."
            size="large"
          />
        ) : (
          <>
            {pager.pageItems.map((req, i) => (
              <ProfessionalRequestRow
                key={req.id}
                req={req}
                showDivider={i > 0}
                acting={actingId === req.id}
                onAccept={() => handleAccept(req.id)}
                onDecline={() => handleDecline(req.id)}
              />
            ))}
            <PaginationBar
              pager={pager}
              itemLabel="requests"
              style={{
                margin: "12px -24px -24px",
                borderRadius: "0 0 8px 8px",
              }}
            />
          </>
        )}
      </section>
    </AppShell>
  );
}

function ProfessionalRequestRow({
  req,
  showDivider,
  acting,
  onAccept,
  onDecline,
}) {
  return (
    <div
      style={{
        padding: "16px 0",
        borderTop: showDivider ? `1px solid ${C.divider}` : "none",
      }}
    >
      <div style={{ marginBottom: 8 }}>
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 12,
            marginBottom: 4,
          }}
        >
          <SafeLogo
            src={req.organisation?.logoDownloadUrl}
            alt={req.organisation?.name || "Organisation"}
            fallbackText={
              req.organisation?.name
                ? req.organisation.name.slice(0, 2).toUpperCase()
                : "ORG"
            }
            imgStyle={{
              width: 36,
              height: 36,
              borderRadius: 8,
              objectFit: "contain",
              border: "1px solid #e2e8f0",
              background: "#ffffff",
              padding: 2,
              flexShrink: 0,
            }}
            fallbackStyle={{
              width: 36,
              height: 36,
              borderRadius: 8,
              background: "#f1f5f9",
              border: "1px solid #e2e8f0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#64748b",
              fontSize: 12,
              fontWeight: 700,
              flexShrink: 0,
            }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 15,
                fontWeight: 700,
                color: C.textDark,
                marginBottom: 2,
              }}
            >
              {req.organisation.name}
            </div>
            {req.organisation.description && (
              <div
                style={{
                  fontSize: 12,
                  color: "#64748b",
                  marginBottom: 4,
                  lineHeight: 1.35,
                }}
              >
                {req.organisation.description}
              </div>
            )}
            {req.organisation.city && (
              <div style={{ fontSize: 12, color: C.textMed }}>
                {req.organisation.city}, {req.organisation.state}
                {req.organisation.websiteUrl && (
                  <>
                    {" "}
                    -{" "}
                    <a
                      href={req.organisation.websiteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: C.blue }}
                    >
                      website
                    </a>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
        {req.appliedForRole && (
          <div style={{ fontSize: 12, color: C.textMed, marginTop: 4 }}>
            Role: <strong>{req.appliedForRole}</strong>
          </div>
        )}
        {req.message && (
          <div
            style={{
              marginTop: 10,
              padding: "10px 12px",
              background: C.bgMuted,
              borderRadius: 4,
              fontSize: 13,
              color: C.textDark,
              lineHeight: 1.5,
            }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: C.textLight,
                textTransform: "uppercase",
                letterSpacing: 0.4,
                marginBottom: 4,
              }}
            >
              Message
            </div>
            {req.message}
          </div>
        )}
        <div style={{ fontSize: 11, color: C.textLight, marginTop: 8 }}>
          Received {formatDate(req.createdAt)}
        </div>
      </div>

      <div
        style={{
          fontSize: 12,
          color: C.textMed,
          marginBottom: 12,
          lineHeight: 1.4,
        }}
      >
        Accepting lets {req.organisation.name} view your verified credentials.
        Contact information is not shared. Every view will be logged and visible
        to you.
      </div>

      <div style={{ display: "flex", gap: 10 }}>
        <button
          onClick={onDecline}
          disabled={acting}
          style={{
            flex: 1,
            padding: "10px",
            fontSize: 13,
            fontWeight: 600,
            color: C.textDark,
            background: "transparent",
            border: `1px solid ${C.dividerStrong}`,
            borderRadius: 4,
            cursor: acting ? "default" : "pointer",
            fontFamily: font,
          }}
        >
          {acting ? "..." : "Decline"}
        </button>
        <button
          onClick={onAccept}
          disabled={acting}
          style={{
            flex: 2,
            padding: "10px",
            fontSize: 13,
            fontWeight: 600,
            color: "white",
            background: acting ? C.textLight : C.green,
            border: 0,
            borderRadius: 4,
            cursor: acting ? "default" : "pointer",
            fontFamily: font,
          }}
        >
          {acting ? "..." : "Accept and share profile"}
        </button>
      </div>
    </div>
  );
}

// ---- shared helpers ----

function StatusPill({ color, bg, label }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "1px 8px",
        fontSize: 10,
        fontWeight: 700,
        color,
        background: bg,
        borderRadius: 10,
        textTransform: "uppercase",
        letterSpacing: 0.4,
      }}
    >
      <span
        style={{ width: 5, height: 5, borderRadius: "50%", background: color }}
      />
      {label}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

const cardStyle = {
  background: C.bg,
  border: `1px solid ${C.divider}`,
  borderRadius: 8,
  padding: 24,
};
